import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangleIcon, XIcon } from 'lucide-react';
import type { ModalIntent, ReassignmentSummary } from '../../types/offboarding';
import { neutralButton, primaryButton } from '../../utils/buttonStyles';
interface ConfirmModalProps {
  intent: ModalIntent | null;
  summary: ReassignmentSummary;
  canConfirm: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  onPreviewOnly: () => void;
}
const EASE = [0.23, 1, 0.32, 1] as const;
export function ConfirmModal({
  intent,
  summary,
  canConfirm,
  onCancel,
  onConfirm,
  onPreviewOnly
}: ConfirmModalProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const open = intent !== null;
  useEffect(() => {
    if (!open) return;
    cancelRef.current?.focus();
    const handle = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', handle);
    return () => window.removeEventListener('keydown', handle);
  }, [open, onCancel]);
  return <AnimatePresence data-id="element-73" data-mp="3s990roxzfils">
      {open && <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-6" data-id="element-74" data-mp="3o7nuimfye2kk">
          <motion.div className="absolute inset-0 bg-gray-900/40" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: EASE
      }} onClick={onCancel} aria-hidden="true" data-id="element-75" data-mp="5t4w4etq76n9" />
          <motion.div role="dialog" aria-modal="true" aria-labelledby="confirm-reassign-title" className="relative flex h-full w-full flex-col bg-white shadow-xl sm:h-auto sm:max-h-[90vh] sm:max-w-lg sm:rounded-xl" initial={{
        opacity: 0,
        scale: 0.96,
        y: 8
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 8
      }} transition={{
        duration: 0.22,
        ease: EASE
      }} data-id="element-76" data-mp="ataq41ngx58i">
            <header className="flex items-start justify-between gap-4 border-b border-gray-100 px-6 py-4" data-id="element-77" data-mp="phxt6mmboy3u">
              <div data-id="element-78" data-mp="ufpjjc7bkc5x">
                <h2 id="confirm-reassign-title" className="text-base font-semibold text-gray-900" data-id="element-79" data-mp="uhaf955de15d">
                  Confirm Record Reassignment
                </h2>
                {intent === 'preview' && <p className="mt-0.5 text-xs text-gray-500" data-id="element-80" data-mp="13iia1d8ogjup">
                    Preview run — nothing changes until you confirm.
                  </p>}
              </div>
              <button type="button" onClick={onCancel} aria-label="Close" className="-mr-2 flex h-8 w-8 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700" data-id="element-81" data-mp="1ddsb9yr94n7f">
                <XIcon className="h-4 w-4" data-id="element-82" data-mp="1r82ww77yc199" />
              </button>
            </header>

            <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5 text-sm text-gray-900" data-id="element-83" data-mp="1w6w7nrvute5q">
              <SummaryBlock title="You are about to reassign records from:" data-id="element-84" data-mp="3goybzn1o1otl">
                {summary.sourceLines.map(line => <li key={line} data-id="element-85" data-mp="3bnhgy85v4wkx">{line}</li>)}
              </SummaryBlock>

              <SummaryBlock title="Objects:" data-id="element-86" data-mp="37nh0khz7ebb8">
                {summary.objectLines.map(line => <li key={line.label} data-id="element-87" data-mp="32pypakxprk02">
                    {line.label}{' '}
                    <span className="font-semibold tabular-nums" data-id="element-88" data-mp="37pbzkkt2ew9">({line.count})</span>
                  </li>)}
              </SummaryBlock>

              <SummaryBlock title="Target:" data-id="element-89" data-mp="3qm2yxp3c5obg">
                {summary.targetLines.length ? summary.targetLines.map(line => <li key={line} data-id="element-90" data-mp="3llx645lbxvrs">{line}</li>) : <li className="text-danger" data-id="element-91" data-mp="mwzt930797wh">Not configured</li>}
                {summary.recipientCount > 0 && <li className="list-none text-xs text-gray-500" data-id="element-92" data-mp="i588xa7mzyes">
                    Round-robin, alphabetical — about {summary.perRecipient} record
                    {summary.perRecipient === 1 ? '' : 's'} per user
                  </li>}
              </SummaryBlock>

              <p className="text-sm" data-id="element-93" data-mp="3ow877ovq0dip">
                <span className="font-medium" data-id="element-94" data-mp="3oxawxthb49x1">Excluded from receiving:</span>{' '}
                {summary.excludedCount === 0 ? 'None' : `${summary.excludedCount} user${summary.excludedCount === 1 ? '' : 's'}`}
              </p>

              <div className="flex gap-3 rounded-lg border border-warning/30 bg-warning/5 p-3.5" data-id="element-95" data-mp="pgl2mrhfvtyp">
                <AlertTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-warning" aria-hidden="true" data-id="element-96" data-mp="bppvbjml9kx8" />
                <p className="text-[13px] text-gray-800" data-id="element-97" data-mp="gmq5a5mk77tm">
                  This action updates record ownership in bulk. A summary email will be sent when
                  processing completes.
                </p>
              </div>
            </div>

            <footer className="flex flex-col gap-2.5 border-t border-gray-100 px-6 py-4 sm:flex-row sm:justify-end sm:gap-3" data-id="element-98" data-mp="1ij47hzngv80b">
              <button ref={cancelRef} type="button" onClick={onCancel} className={neutralButton} data-id="element-99" data-mp="1eibztau8tb65">
                Cancel
              </button>
              <button type="button" onClick={onPreviewOnly} className={neutralButton} data-id="element-100" data-mp="1s85ljkagbfbn">
                Preview Only
              </button>
              <button type="button" onClick={onConfirm} disabled={!canConfirm} className={primaryButton} data-id="element-101" data-mp="zk000vbkpmar">
                Confirm &amp; Reassign
              </button>
            </footer>
          </motion.div>
        </div>}
    </AnimatePresence>;
}
function SummaryBlock({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <div data-id="element-102" data-mp="2kwb7299bx8cj">
      <p className="mb-1.5 font-medium" data-id="element-103" data-mp="2kw1k6ujkzhb2">{title}</p>
      <ul className="list-disc space-y-1 pl-5 marker:text-gray-400" data-id="element-104" data-mp="2fz1bebvck0g9">{children}</ul>
    </div>;
}